<template>
  <div class="flex h-screen min-h-0 w-full flex-col overflow-hidden text-white antialiased md:flex-row">
    <PlusAppSidebar />
    <div class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <!-- 背景：暗色点阵 -->
      <div class="pointer-events-none absolute inset-0 bg-[#07070a]"></div>
      <div
        class="pointer-events-none absolute inset-0 opacity-90"
        style="background-image:radial-gradient(circle at 1px 1px, rgba(255,255,255,0.10) 1px, rgba(0,0,0,0) 1px);background-size:22px 22px;"
      ></div>
      <div class="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/35 via-black/10 to-black/60"></div>

      <main class="relative z-10 mx-auto w-full max-w-[1400px] flex-1 overflow-y-auto px-5 py-8 sm:px-8">
      <!-- 顶部 -->
      <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">我的资产</h1>
          <div class="mt-4 inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10 backdrop-blur">
            <button @click="activeTab = 'all'" :class="tabBtnClass(activeTab === 'all')">全部</button>
            <button @click="activeTab = 'image'" :class="tabBtnClass(activeTab === 'image')">角色</button>
            <button @click="activeTab = 'scene'" :class="tabBtnClass(activeTab === 'scene')">场景</button>
          </div>
        </div>

        <div class="w-full sm:w-[360px] flex items-center gap-3">
          <div class="flex-1">
            <div
              class="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 backdrop-blur focus-within:border-fuchsia-500/40 transition-colors"
            >
              <i class="fas fa-search text-gray-400 text-sm"></i>
              <input
                v-model.trim="keyword"
                class="w-full bg-transparent outline-none text-sm text-gray-200 placeholder:text-gray-500"
                placeholder="搜索资产名称"
                autocomplete="off"
              >
              <button
                v-if="keyword"
                @click="keyword = ''"
                class="text-gray-400 hover:text-gray-200 transition-colors"
                title="清空"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- 网格 -->
      <section class="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
        <!-- 新建（占位：后续可接“角色创建流程”） -->
        <button
          type="button"
          @click="goCreate"
          class="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-fuchsia-500/35 hover:bg-white/7 transition-colors text-left"
        >
          <div class="aspect-[4/5] w-full flex items-center justify-center bg-gradient-to-br from-white/4 to-white/0">
            <div class="w-12 h-12 rounded-2xl bg-white/6 border border-white/10 flex items-center justify-center text-white/90 group-hover:scale-[1.02] transition-transform">
              <i class="fas fa-plus text-lg"></i>
            </div>
          </div>
          <div class="p-3.5">
            <div class="font-bold text-sm text-white/90">创建新角色</div>
            <div class="mt-1 text-xs text-gray-400">从图片资产开始</div>
          </div>
        </button>

        <!-- 资产卡片 -->
        <article
          v-for="a in filteredAssets"
          :key="a.id"
          class="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/7 transition-colors"
        >
          <button
            v-if="a.url"
            type="button"
            class="block w-full text-left"
            @click="openPreview(a.url)"
            title="点击预览"
          >
            <div class="aspect-[4/5] w-full bg-black/25 relative">
              <img :src="a.url" alt="" class="w-full h-full object-cover opacity-95 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
          <div v-else class="aspect-[4/5] w-full bg-black/25 flex items-center justify-center text-white/15">
            <i class="fas fa-photo-film text-3xl"></i>
          </div>

          <!-- NO 标签 -->
          <div class="absolute top-2 left-2 px-2 py-1 rounded-lg bg-black/55 border border-white/10 text-[10px] text-gray-200 font-black tracking-wide">
            NO.
            <span class="text-white/90">{{ shortId(a.id) }}</span>
          </div>

          <!-- 删除 -->
          <button
            type="button"
            class="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/55 border border-white/10 text-white/70 hover:text-white hover:bg-black/70 transition-colors"
            title="删除"
            @click="removeAsset(a.id)"
          >
            <i class="fas fa-times text-[12px]"></i>
          </button>

          <div class="p-3">
            <div class="font-bold text-[12px] text-white/90 truncate text-center">
              {{ a.title || '未命名' }}
            </div>
          </div>

          <!-- 霓虹描边（接近截图那种） -->
          <div class="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/5"></div>
          <div
            class="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
            style="box-shadow:0 0 0 2px rgba(236,72,153,0.25), 0 0 36px rgba(236,72,153,0.20);"
          ></div>
        </article>
      </section>

      <div v-if="!filteredAssets.length" class="mt-16 text-center text-gray-400">
        <div class="text-white/20 text-4xl mb-3">
          <i class="fas fa-box-open"></i>
        </div>
        <div class="font-bold text-sm text-gray-200">暂无资产</div>
        <div class="mt-1 text-xs">在对话工作流生成图片后点击“加入资产”即可</div>
      </div>
    </main>

    <!-- 预览 -->
    <div
      v-if="showPreview"
      class="fixed inset-0 z-[1200] bg-black/70 backdrop-blur-[2px] flex items-center justify-center p-6"
      @click="closePreview"
    >
      <div class="relative max-w-[min(92vw,1100px)] max-h-[88vh]" @click.stop>
        <button
          type="button"
          class="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-black/60 border border-white/10 text-white/80 hover:text-white hover:bg-black/80 transition-colors"
          title="关闭"
          @click="closePreview"
        >
          <i class="fas fa-times"></i>
        </button>
        <img
          v-if="previewUrl"
          :src="previewUrl"
          alt=""
          class="max-w-full max-h-[88vh] object-contain rounded-2xl shadow-2xl"
        />
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import PlusAppSidebar from '../components/PlusAppSidebar.vue'
import { showSuccessToast, showErrorToast } from '@/utils/toast'
import { fetchAssetLibraryList, deleteAssetFromLibrary } from '@/utils/assetLibraryApi'
import { saveCanvasNodeHandoff } from '@/utils/canvasNodeHandoff'

function safeJsonParse(s, fallback) {
  try {
    const v = JSON.parse(s)
    return v == null ? fallback : v
  } catch {
    return fallback
  }
}

export default {
  name: 'Assets',
  components: {
    PlusAppSidebar
  },
  data() {
    return {
      activeTab: 'all',
      keyword: '',
      assets: [],
      showPreview: false,
      previewUrl: ''
    }
  },
  computed: {
    filteredAssets() {
      const kw = (this.keyword || '').toLowerCase()
      return (this.assets || [])
        .filter(a => {
          if (this.activeTab === 'all') return true
          // 截图里是“角色/场景”，这里先用 kind 做占位映射：image→角色，scene→场景
          if (this.activeTab === 'image') return (a.kind || 'image') === 'image'
          if (this.activeTab === 'scene') return (a.kind || '') === 'scene'
          return true
        })
        .filter(a => {
          if (!kw) return true
          return String(a.title || '').toLowerCase().includes(kw)
        })
    }
  },
  mounted() {
    this.loadAssets()
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    tabBtnClass(active) {
      return [
        'px-4 py-2 rounded-full text-xs font-bold transition-all',
        active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-200'
      ]
    },
    shortId(id) {
      const s = String(id || '')
      return s.length > 8 ? s.slice(-6) : s
    },
    async loadAssets() {
      // 优先读数据库；失败则退回本地缓存
      try {
        const rows = await fetchAssetLibraryList({ limit: 200 })
        this.assets = (rows || [])
          .map((x, idx) => ({
            id: x.id != null ? String(x.id) : `asset-${idx}`,
            kind: x.kind || 'image',
            url: x.url || '',
            title: x.title || '未命名',
            createdAt: x.created_at || x.createdAt || '',
          }))
          .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
        return
      } catch (e) {
        // ignore and fallback
      }

      const raw = localStorage.getItem('assetLibrary')
      const arr = safeJsonParse(raw || '[]', [])
      this.assets = (Array.isArray(arr) ? arr : [])
        .map((x, idx) => ({
          id: x.id || `asset-${idx}`,
          kind: x.kind || 'image',
          url: x.url || x.imageUrl || '',
          title: x.title || x.name || '未命名',
          createdAt: x.createdAt || ''
        }))
        .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
    },
    persistLocalFallback() {
      localStorage.setItem('assetLibrary', JSON.stringify(this.assets || []))
    },
    async removeAsset(id) {
      const sid = String(id || '')
      if (!sid) return
      try {
        await deleteAssetFromLibrary(sid)
        this.assets = (this.assets || []).filter(x => String(x.id || '') !== sid)
        showSuccessToast('已删除')
      } catch (e) {
        // 数据库删除失败时，仍允许本地删（兜底）
        this.assets = (this.assets || []).filter(x => String(x.id || '') !== sid)
        this.persistLocalFallback()
        showSuccessToast('已删除（本地兜底）')
      }
    },
    openPreview(url) {
      const u = String(url || '').trim()
      if (!u) return
      this.previewUrl = u
      this.showPreview = true
    },
    closePreview() {
      this.showPreview = false
      this.previewUrl = ''
    },
    onKeydown(e) {
      if (e && e.key === 'Escape' && this.showPreview) this.closePreview()
    },
    goCreate() {
      const ok = saveCanvasNodeHandoff({
        nodeType: 'image',
        prompt: '',
        autoRun: false
      })
      if (!ok) {
        showErrorToast('无法跳转，请重试')
        return
      }
      this.$router
        .push({ path: '/workspace', query: { node: '1', handoff: '1' } })
        .catch(() => {
          showErrorToast('跳转失败')
        })
    }
  }
}
</script>

<style scoped></style>

